import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Exportar, { descargarArchivo } from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import BotonAyuda from '../components/BotonAyuda';
import ComprobanteRetencion from '../components/ComprobanteRetencion';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

const TIPO_LABEL = { iva: 'IVA', islr: 'ISLR', municipal: 'Municipal' };

function pad(n) { return String(n).padStart(2, '0'); }
function iso(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }

// Quincena que contiene hoy: 1-15 o 16-fin de mes.
function quincenaActual() {
  const hoy = new Date();
  const y = hoy.getFullYear(), m = hoy.getMonth(), d = hoy.getDate();
  if (d <= 15) return { desde: iso(y, m, 1), hasta: iso(y, m, 15) };
  const ultimoDia = new Date(y, m + 1, 0).getDate();
  return { desde: iso(y, m, 16), hasta: iso(y, m, ultimoDia) };
}

function quincenaAnterior() {
  const actual = quincenaActual();
  const [ay, am, ad] = actual.desde.split('-').map(Number);
  if (ad === 1) {
    // Estábamos en la 1ra quincena -> la anterior es la 2da del mes previo.
    const mesPrev = am - 2; // am es 1-indexado en el string; Date usa 0-indexado
    const y = mesPrev < 0 ? ay - 1 : ay;
    const m = (mesPrev + 12) % 12;
    const ultimoDia = new Date(y, m + 1, 0).getDate();
    return { desde: iso(y, m, 16), hasta: iso(y, m, ultimoDia) };
  }
  return { desde: iso(ay, am - 1, 1), hasta: iso(ay, am - 1, 15) };
}

// Desde Impuestos llega embebida con el mes del resumen (desdeInicial/hastaInicial).
export default function Retenciones({ embebido = false, desdeInicial, hastaInicial } = {}) {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas?.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;
  const [rango, setRango] = useState(desdeInicial ? { desde: desdeInicial, hasta: hastaInicial } : quincenaActual());
  const [tipo, setTipo] = useState('');
  const [detalle, setDetalle] = useState([]);
  const [totales, setTotales] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [abierta, setAbierta] = useState(null); // id del comprobante abierto
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    if (!empresaActiva) return;
    setCargando(true);
    api.get('/reportes/retenciones', { params: { desde: rango.desde, hasta: rango.hasta, tipo: tipo || undefined } })
      .then((res) => { setDetalle(res.data.detalle); setTotales(res.data.totales); })
      .finally(() => setCargando(false));
  }, [empresaActiva, rango, tipo, recarga]);

  async function exportarExcel() {
    const { data } = await api.get('/reportes/retenciones/excel', { params: { desde: rango.desde, hasta: rango.hasta, tipo: tipo || undefined }, responseType: 'blob' });
    descargarArchivo(data, `retenciones${empresaNombre ? `-${empresaNombre}` : ''}-${rango.desde}-${rango.hasta}.xlsx`);
  }


  const totalGeneral = totales.reduce((s, t) => s + Number(t.monto || 0), 0);

  return (
    <div className="space-y-6">
      <EncabezadoImpresion titulo="Retenciones" detalle={`${tipo ? `${{ iva: 'IVA', islr: 'ISLR', municipal: 'Municipal' }[tipo]} · ` : ''}del ${formatFecha(rango.desde)} al ${formatFecha(rango.hasta)}`} />
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div>
          {!embebido && <h1 className="text-2xl font-bold text-slate-800">Retenciones<BotonAyuda /></h1>}
          <p className="text-sm text-slate-500">IVA, ISLR y Municipal calculadas en facturas de compra — para enterar. #35/#97</p>
        </div>
        <Exportar onExcel={exportarExcel} />
      </div>

      <div className="flex items-end gap-3 flex-wrap print:hidden">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Desde</label>
          <input type="date" value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value })} className="border rounded-md px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Hasta</label>
          <input type="date" value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value })} className="border rounded-md px-2 py-1.5 text-sm" />
        </div>
        <div className="flex gap-1">
          <button onClick={() => setRango(quincenaAnterior())} className="px-3 py-1.5 rounded-md text-sm border text-slate-600 hover:bg-slate-50">
            Quincena anterior
          </button>
          <button onClick={() => setRango(quincenaActual())} className="px-3 py-1.5 rounded-md text-sm border text-slate-600 hover:bg-slate-50">
            Quincena actual
          </button>
        </div>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm ml-auto">
          <option value="">Todos los tipos</option>
          <option value="iva">IVA</option>
          <option value="islr">ISLR</option>
          <option value="municipal">Municipal</option>
        </select>
      </div>

      {cargando ? (
        <p className="text-slate-500">Cargando...</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            {totales.map((t) => (
              <div key={t.tipo} className="bg-white rounded-xl shadow-sm p-4">
                <p className="text-xs text-slate-500">Retención {t.label}</p>
                <p className="text-xl font-semibold text-slate-800">{formatMonto(t.monto)}</p>
                <p className="text-xs text-slate-400">{t.cantidad} factura{t.cantidad === 1 ? '' : 's'}</p>
              </div>
            ))}
            <div className="bg-primary-50 rounded-xl shadow-sm p-4">
              <p className="text-xs text-primary-700">Total a enterar</p>
              <p className="text-xl font-semibold text-primary-800">{formatMonto(totalGeneral)}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="text-left p-2">Tipo</th>
                  <th className="text-left p-2">Fecha</th>
                  <th className="text-left p-2">Proveedor</th>
                  <th className="text-left p-2">N° Factura</th>
                  <th className="text-left p-2">Concepto</th>
                  <th className="text-right p-2">Base imponible</th>
                  <th className="text-right p-2">%</th>
                  <th className="text-right p-2">Monto retenido</th>
                  <th className="text-left p-2">N° Comprobante</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {detalle.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-slate-400 p-6">Sin retenciones en ese rango.</td></tr>
                ) : detalle.map((r) => (
                  <tr key={r.id} {...filaClickeable(() => setAbierta(r.id))} title="Abrir comprobante" className={`cursor-pointer hover:bg-slate-50 transition-colors ${FOCO_FILA}`}>
                    <td className="p-2">{TIPO_LABEL[r.tipo]}</td>
                    <td className="p-2">{formatFecha(r.fecha)}</td>
                    <td className="p-2">{r.proveedor_nombre}</td>
                    <td className="p-2">{r.factura_numero}</td>
                    <td className="p-2">{r.islr_concepto || '—'}</td>
                    <td className="p-2 text-right">{formatMonto(r.base_imponible)}</td>
                    <td className="p-2 text-right">{r.porcentaje != null ? `${r.porcentaje}%` : '—'}</td>
                    <td className="p-2 text-right">{formatMonto(r.monto)}</td>
                    <td className="p-2 text-primary-600 font-medium">{r.numero_comprobante}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {abierta && (
        <ComprobanteRetencion retencionId={abierta} onCerrar={() => setAbierta(null)} onGuardado={() => setRecarga((n) => n + 1)} />
      )}
    </div>
  );
}
